import type { CommandName } from "./types";

/**
 * Версионирование локального хранилища — ТРИ независимых механизма, а не одно число
 * (offline-sync §2.1). В IndexedDB лежат вещи с разной ценой потери: реплику снести
 * дёшево (один снапшот), а очередь — это работа человека оффлайн, и она невосстановима.
 *
 * Руками бампаются ровно две вещи: нативная версия IDB (при появлении стора)
 * и `REPLICA_EPOCH`. Всё остальное считается.
 */

/**
 * Ручная соль на случай, когда **форма та же, а смысл другой**: `scheduledDate` был
 * в UTC, стал локальной датой — тип `string` в обоих случаях, отпечаток не дрогнет.
 * Это единственное, что здесь бампается головой.
 */
export const REPLICA_EPOCH = "1";

/**
 * Форма реплики, как её понимает КЛИЕНТ. Отпечаток считается отсюда, а не из номера
 * миграции: номер описывает серверное состояние, между ним и стором лежит слой DTO —
 * он пропускает правки проекции и ложно срабатывает на индексах и грантах.
 */
const REPLICA_SHAPE = {
	nodes: [
		"id",
		"parentId",
		"position",
		"role",
		"title",
		"body",
		"status",
		"scheduledDate",
		"dayDate",
		"completedAt",
		"archivedAt",
		"archivedOp",
		"recurrence",
		"xp",
		"difficulty",
		"tags",
		"contentVersion",
		"createdAt",
		"updatedAt"
	],
	placements: ["id", "nodeId", "containerId", "position", "createdAt"],
	links: ["id", "fromNodeId", "toNodeId", "relation", "createdAt"],
	completions: ["id", "nodeId", "completedOn", "clientEventId", "xp", "createdAt"],
	tags: ["id", "name", "color", "updatedAt"]
} as const;

/**
 * Форма входа каждой команды. Отпечаток берётся НА ТИП, а не на очередь целиком:
 * общий хэш по объединению менялся бы при добавлении новой команды у всех сразу,
 * и безупречная очередь уезжала бы в разбор при каждом релизе.
 */
const COMMAND_SHAPES: Record<CommandName, readonly string[]> = {
	createNode: ["id", "parentId", "title", "role", "body", "place"],
	insertSubtree: ["parentId", "nodes", "place"],
	renameNode: ["nodeId", "title", "baseContentVersion"],
	setBody: ["nodeId", "body", "baseContentVersion"],
	moveNodes: ["nodeIds", "parentId", "after", "before"],
	archiveNodes: ["nodeIds"],
	restoreNodes: ["nodeIds"],
	deleteNodes: ["nodeIds", "op"],
	restoreDeleted: ["op"],
	addPlacement: ["nodeId", "containerId", "place"],
	removePlacement: ["nodeId", "containerId"],
	movePlacement: ["placementId", "after", "before"],
	setStatusMany: ["nodeIds", "status", "skipTerminal"],
	convertRole: ["nodeId", "role"],
	schedule: ["nodeId", "date"],
	scheduleForToday: ["nodeId"],
	setDifficulty: ["nodeId", "difficulty"],
	setRecurrence: ["nodeId", "recurrence"],
	setTags: ["nodeId", "tags"],
	setTagColor: ["tag", "color"],
	completeNode: ["nodeId", "on", "clientEventId"],
	uncompleteNode: ["nodeId", "on", "clientEventId"],
	ensureDay: ["date"],
	setTimezone: ["tz"]
};

/**
 * FNV-1a, а не SHA-256.
 *
 * Задача отпечатка — заметить изменение формы, а не устоять против подбора: подделывать
 * его некому, он никогда не покидает браузер. Криптохэш здесь означал бы асинхронный
 * `crypto.subtle` в синхронном пути старта — цена без выигрыша.
 */
function hash(input: string): string {
	let h = 0x811c9dc5;
	for (let i = 0; i < input.length; i += 1) {
		h ^= input.charCodeAt(i);
		h = Math.imul(h, 0x01000193) >>> 0;
	}
	return h.toString(16).padStart(8, "0");
}

export function replicaFingerprint(): string {
	return hash(JSON.stringify(REPLICA_SHAPE) + REPLICA_EPOCH);
}

export function commandFingerprint(name: CommandName): string {
	return hash(`${name}:${JSON.stringify(COMMAND_SHAPES[name] ?? [])}`);
}
